// useAuth.ts — Shared authentication hook for openfactory web apps.
// Generalizes the per-app `useAuth` implementations that used to live in
// cooking, objectifio, and authz. Apps supply their own ApiClient (which
// encapsulates the app-specific token storage key), plus the /me endpoint
// path they expose on their backend.
//
// On a 401 from /me with a token actually presented, the hook logs *why*
// the server rejected it (from the response body, populated by the
// lib/oidcrp middleware) and clears the local token so the SPA's router
// can bounce the user back through the login flow. Cookie-based sessions
// don't trigger the "explanatory" path — a missing cookie on first paint
// is the unauthenticated bootstrap case, not an invalidation.

import { useState, useEffect, useCallback } from 'react';
import type { ApiClient } from './apiClient';
import { peekAuthFailure } from './apiClient';

export interface AuthIdentity {
    email: string;
    roles: string[];
    scopes: string[];
    photoUrl: string;
    // Whatever else the service's /me returned, verbatim. Services answer
    // service-specific questions there — "may this session author?" is one the
    // server can settle far more cheaply than a client re-deriving it from
    // roles and scopes — and this carries those fields through without
    // teaching the shared hook about any of them.
    extra: Record<string, unknown>;
}

export interface AuthState extends AuthIdentity {
    loading: boolean;
    error: string;
    authenticated: boolean;
}

export interface UseAuthOptions {
    client: ApiClient;
    // Path to the identity endpoint, e.g. "/api/me".
    mePath?: string;
}

const INITIAL: AuthState = {
    email: '',
    roles: [],
    scopes: [],
    photoUrl: '',
    extra: {},
    loading: true,
    error: '',
    authenticated: false,
};

export function useAuth(options: UseAuthOptions): AuthState & { refresh: () => Promise<void>; logout: () => void } {
    const { client, mePath = '/api/me' } = options;
    const [state, setState] = useState<AuthState>(INITIAL);

    const refresh = useCallback(async () => {
        try {
            // Always probe mePath. fetch() forwards credentials so cookie-
            // based sessions (set by the service's /api/oidc/callback) work
            // without a localStorage token; bearer-only apps still work
            // because getToken() populates the Authorization header inside
            // apiClient.fetch when present. The previous early-return on
            // !getToken() broke cookie-only flows by short-circuiting to
            // INITIAL before we ever learned the cookie was valid.
            const hadToken = Boolean(client.getToken());
            const res = await client.fetch(mePath);
            if (res.status === 401) {
                // If we actually sent a token, the server rejected it — log
                // *why* (from the response body) and drop the local token
                // so the next render doesn't keep waving an invalid Bearer.
                // Cookie-based sessions hit this path too, but their cookie
                // is httpOnly and only the server can clear it — useAuth's
                // job is just to surface the reason; the SPA's router or
                // an explicit logout button handles the cookie via the
                // server's /api/oidc/logout endpoint.
                if (hadToken) {
                    const reason = await peekAuthFailure(res, 'token rejected');
                    console.error(
                        `[authz] /api/me returned 401: ${reason}. Clearing stored token; re-authentication required.`,
                    );
                    client.clearToken();
                    setState({ ...INITIAL, loading: false, error: reason });
                    return;
                }
                setState({ ...INITIAL, loading: false });
                return;
            }
            if (!res.ok) {
                throw new Error(`Failed to load profile (${res.status})`);
            }
            const data = await res.json() as Partial<AuthIdentity> & { photo_url?: string };
            setState({
                email: data.email ?? '',
                roles: data.roles ?? [],
                scopes: data.scopes ?? [],
                // Server-side /api/me follows JSON-snake-case (`photo_url`);
                // accept either spelling so apps that wrap the call don't
                // need to remap.
                photoUrl: data.photoUrl ?? data.photo_url ?? '',
                extra: data as Record<string, unknown>,
                loading: false,
                error: '',
                authenticated: Boolean(data.email),
            });
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to load profile';
            setState({ ...INITIAL, loading: false, error: msg });
        }
    }, [client, mePath]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const logout = useCallback(() => {
        client.clearToken();
        setState({ ...INITIAL, loading: false });
    }, [client]);

    return { ...state, refresh, logout };
}
